/**
 * Toast Factory
 * 
 * Factory pattern implementation for creating toast objects.
 * Provides a fluent API for toast configuration with sensible defaults.
 * 
 * @example
 * // Simple usage
 * ToastFactory.success('Saved', 'Your changes have been saved').build();
 * 
 * @example
 * // With actions
 * ToastFactory.error('Failed', 'Could not save changes')
 *   .withActions([{ label: 'Retry', onClick: handleRetry }])
 *   .persistent()
 *   .build();
 * 
 * @example
 * // Custom duration
 * ToastFactory.info('Update Available')
 *   .withDuration(10000)
 *   .build();
 */

import { TOAST_TYPES, TOAST_DURATIONS } from './toastConstants';

let toastIdCounter = 0;

/**
 * Generate unique toast ID
 * @returns {string} Unique toast identifier
 */
const generateToastId = () => {
    toastIdCounter += 1;
    return `toast-${Date.now()}-${toastIdCounter}`;
};

/**
 * Toast Builder class for fluent API
 */
class ToastBuilder {
    constructor(type, title, content = '') {
        this.toast = {
            id: generateToastId(),
            type,
            title,
            content,
            timestamp: new Date(),
            actions: [],
            autoClose: true,
            duration: TOAST_DURATIONS.DEFAULT,
        };
    }

    /**
     * Add action buttons to the toast
     * @param {Array<{label: string, onClick: Function}>} actions - Action buttons
     * @returns {ToastBuilder}
     */
    withActions(actions) {
        this.toast.actions = actions;
        return this;
    }

    /**
     * Add a single action button
     * @param {string} label - Button label
     * @param {Function} onClick - Click handler
     * @returns {ToastBuilder}
     */
    withAction(label, onClick) {
        this.toast.actions.push({ label, onClick });
        return this;
    }

    /**
     * Set custom duration in milliseconds
     * @param {number} ms - Duration in milliseconds
     * @returns {ToastBuilder}
     */
    withDuration(ms) {
        this.toast.duration = ms;
        return this;
    }

    /**
     * Make toast persistent (won't auto-close)
     * @returns {ToastBuilder}
     */
    persistent() {
        this.toast.autoClose = false;
        this.toast.duration = null;
        return this;
    }

    /**
     * Set short duration (3 seconds)
     * @returns {ToastBuilder}
     */
    short() {
        this.toast.duration = TOAST_DURATIONS.SHORT;
        return this;
    }

    /**
     * Set long duration (8 seconds)
     * @returns {ToastBuilder}
     */
    long() {
        this.toast.duration = TOAST_DURATIONS.LONG;
        return this;
    }

    /**
     * Add custom data to the toast
     * @param {Object} data - Custom data
     * @returns {ToastBuilder}
     */
    withData(data) {
        this.toast.data = data;
        return this;
    }

    /**
     * Build and return the toast object
     * @returns {Object} Toast object ready for dispatch
     */
    build() {
        return { ...this.toast };
    }
}

/**
 * Toast Factory with static methods for each toast type
 */
const ToastFactory = {
    /**
     * Create a success toast
     * @param {string} title - Toast title
     * @param {string} [content] - Toast content/description
     * @returns {ToastBuilder}
     */
    success(title, content = '') {
        return new ToastBuilder(TOAST_TYPES.SUCCESS, title, content);
    },

    /**
     * Create an error toast
     * @param {string} title - Toast title
     * @param {string} [content] - Toast content/description
     * @returns {ToastBuilder}
     */
    error(title, content = '') {
        return new ToastBuilder(TOAST_TYPES.ERROR, title, content);
    },

    /**
     * Create a warning toast
     * @param {string} title - Toast title
     * @param {string} [content] - Toast content/description
     * @returns {ToastBuilder}
     */
    warning(title, content = '') {
        return new ToastBuilder(TOAST_TYPES.WARNING, title, content);
    },

    /**
     * Create an info toast
     * @param {string} title - Toast title
     * @param {string} [content] - Toast content/description
     * @returns {ToastBuilder}
     */
    info(title, content = '') {
        return new ToastBuilder(TOAST_TYPES.INFO, title, content);
    },

    /**
     * Create a normal/default toast
     * @param {string} title - Toast title
     * @param {string} [content] - Toast content/description
     * @returns {ToastBuilder}
     */
    normal(title, content = '') {
        return new ToastBuilder(TOAST_TYPES.NORMAL, title, content);
    },

    /**
     * Create a custom toast with any type
     * @param {string} type - Toast type
     * @param {string} title - Toast title
     * @param {string} [content] - Toast content/description
     * @returns {ToastBuilder}
     */
    create(type, title, content = '') {
        return new ToastBuilder(type, title, content);
    },
};

export { ToastFactory, ToastBuilder, generateToastId };
export default ToastFactory;

